import React, { useEffect } from 'react'
import { RunSearch } from 'schemas/action/RunSearchSchema'
import { Dashboard } from 'schemas/DashboardSchema'
import { getAllMeasureTypes } from 'utils/summaryUtil'
import { toFilter } from 'utils/widgetUtil'
import { getMetricCategory } from 'utils/MetricItemUtil'
import { isAudienceAnalysis, isYoutubeOnly } from 'components/widget/utils'
import { MetricOptions } from 'features/widget/types'
import { createMeasureMetric, createMetric } from 'utils/runSearchUtil'
import MetricTimeUnit from 'enums/MetricTimeUnit'
import MeasureType, { MeasureTypeStrings } from 'enums/MeasureType'
import { useDashboardData, useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { useAppDispatch } from 'app/hooks'
import { getWidgetForDashboard } from 'features/widget/asyncThunks'
import { getMetricSeries } from 'utils/measureTypeUtil'
import useRefreshTimestamp from 'hooks/useRefreshTimestamp'

import { SummaryMetricsType } from '../../types'

const createSearches = (
  widgetSetting: SummaryMetricsType,
  dashboardData: Dashboard
): { searches: RunSearch[]; searchesIdx: Record<string, number> } => {
  const { id: dashboardId, name: dashboardName } = dashboardData
  const {
    metricNames,
    measures,
    selectedTopic,
    analysisType,
    viewOption,
    precision,
    showChange
  } = widgetSetting
  // Extract all measure types.
  const searchMeasures = getAllMeasureTypes(
    analysisType!,
    viewOption!,
    measures as MeasureTypeStrings[]
  )

  // Handle time filter.
  const filter = toFilter(widgetSetting)
  const start = filter.publishedDate.start!
  const end = filter.publishedDate.end!
  const period = end - start

  // Handle delta time filter (to calculate previous period).
  const deltaFilter = toFilter(widgetSetting)
  deltaFilter.publishedDate.start = start - period
  deltaFilter.publishedDate.end = start - 1

  // Handle topic filter.
  const topicIds = [selectedTopic.topicId!]

  // Create searches.
  const searches = []
  let deltaIdx = -1
  let countIdx = -1

  const metricCategory = getMetricCategory(viewOption)
  const option: MetricOptions = {}
  if (metricCategory) {
    option.metricCategory = metricCategory
  }
  option.isAudienceQuery = isAudienceAnalysis(analysisType)
  option.isYoutubeOnly = isYoutubeOnly(analysisType)

  // Search for measure
  if (searchMeasures && searchMeasures.length > 0) {
    searches.push(
      createMetric(
        dashboardId!,
        dashboardName!,
        precision!,
        topicIds!,
        filter,
        searchMeasures.flatMap((measure) => getMetricSeries(measure!)),
        [MetricTimeUnit.ALL],
        option
      )
    )
  }
  // Search for measurements (VoC)
  if (metricNames && metricNames.length > 0) {
    searches.push(
      createMeasureMetric(
        dashboardId!,
        dashboardName!,
        precision!,
        topicIds,
        filter,
        metricNames as string[],
        [MetricTimeUnit.ALL],
        option
      )
    )
  }
  // Search for delta
  if (showChange && deltaFilter.publishedDate.start >= 0) {
    deltaIdx = searches.length
    if (searchMeasures && searchMeasures.length > 0) {
      searches.push(
        createMetric(
          dashboardId!,
          dashboardName!,
          precision!,
          topicIds!,
          deltaFilter,
          searchMeasures.flatMap((measure) => getMetricSeries(measure!)),
          [MetricTimeUnit.ALL],
          option
        )
      )
    }
    if (metricNames && metricNames!.length > 0) {
      searches.push(
        createMeasureMetric(
          dashboardId!,
          dashboardName!,
          precision!,
          topicIds,
          deltaFilter,
          metricNames as string[],
          [MetricTimeUnit.ALL],
          option
        )
      )
    }
  }
  // search for mention
  if (!searchMeasures || !searchMeasures.includes(MeasureType.MENTIONS)) {
    countIdx = searches.length
    const mOption: MetricOptions = { isYoutubeOnly: isYoutubeOnly(analysisType) }
    searches.push(
      createMetric(
        dashboardId!,
        dashboardName!,
        precision!,
        topicIds!,
        filter,
        [MeasureType.MENTIONS].flatMap((measure) => getMetricSeries(measure!)),
        [MetricTimeUnit.ALL],
        mOption
      )
    )
  }

  return { searches, searchesIdx: { deltaIdx, countIdx } }
}

interface RunSearchEffectProps {
  widgetId: number
  children: React.ReactElement
}

const RunSearchEffect = ({ widgetId, children }: RunSearchEffectProps): React.ReactElement => {
  const widgetSetting = useDashboardWidgetSetting<SummaryMetricsType>(widgetId)
  const dashboardData = useDashboardData()
  const dispatch = useAppDispatch()
  const refreshTimestamp = useRefreshTimestamp(widgetId)

  useEffect(() => {
    const { searches, searchesIdx } = createSearches(widgetSetting, dashboardData!)
    dispatch(
      getWidgetForDashboard({
        widgetId,
        searches,
        searchesIdx,
        timestamp: refreshTimestamp
      })
    )
  }, [widgetSetting, dashboardData, widgetId, dispatch, refreshTimestamp])

  return children
}

export default RunSearchEffect
